<template>
    <!--
        Container fluid pour qu'il prenne toute la largeur disponible (flex)
        Les évènements @click et @contextmenu sont géré sur le b-container pour gérer la fermeture de menus contextuels déjà ouverts
    -->
    <b-container @click="clearMenuHandler" @contextmenu="contextMenuHandler" @drop="hideTooltip" fluid class="d-flex flex-column mh-100 h-100 p-0 no-gutters">
        <b-row id="WorkbenchRow1" cols="12" class="py-2 no-gutters bg-gray-light">
            <b-col order="3" order-sm="1" class="p-0 no-gutters">
                <!-- Chemin du noeud sélectionné en single-node -->
                <Navbar :ManageGrouping="currentNode.ObjectType === baseObjectTypeEnum.Client" :InGrouping.sync="inGrouping" />
            </b-col>
            <b-col order="1" order-sm="2" :cols="this.$screen.breakpoint === 'xs' && this.$screen.portrait ? 10 : 'auto'" :class="['align-items-center', { 'h-100 pr-3': !(this.$screen.breakpoint === 'xs' && this.$screen.portrait), 'pb-3 pl-3': this.$screen.breakpoint === 'xs' && this.$screen.portrait}]">
                <!-- Menu de fonctions -->
                <Features :Variant="Variant" :maxHeightMenu="checkSmPortrait() ? '80dvh': '87dvh'"
                    :class="['d-flex', { 'justify-content-end': !(this.$screen.breakpoint === 'xs' && this.$screen.portrait) }, {'invisible' : inGrouping }]"/> <!-- Ajustement de hauteur pour les petits écrans -->
            </b-col>
            <b-col v-if="($screen.breakpoint === 'xs' || $screen.breakpoint === 'sm')" order="2" cols="2" sm="1" class="py-0 pl-0 text-center">
                <!-- Bouton de masquage de la sidebar affiché sur smartphone pour masquer -->
                <b-button v-b-tooltip.hover.bottom :title="isSidebarCollapsed ? $t('ShowFunction') : $t('ShowTreeStructure')" @click="toggleSidebarCollapsed()" variant="secondary" size="md" pill>
                    <b-icon v-if="isSidebarCollapsed" icon="caret-right-fill" />
                    <font-awesome-icon v-else icon="folder-tree" />
                </b-button>
            </b-col>
        </b-row>

        <!-- Version écran mobile/smartphone -->
        <b-row v-if="($screen.breakpoint === 'xs' || $screen.breakpoint === 'sm')" id="WorkbenchRow2Mobile" class="flex-grow-1 w-100 overflow-auto p-0 no-gutters">
            <b-col v-show="isSidebarCollapsed" class="p-0 no-gutters h-100 w-100">
                <FullSidebar :ManageGrouping="currentNode.ObjectType === baseObjectTypeEnum.Client" :InGrouping="inGrouping" @contextmenu.stop.prevent @DisplayContextMenuEvent="(...args) => contextMenuHandler(...args, saveMode!==saveModeEnum.NewObject)" v-on:ToggleSidebarCollapsedEvent="toggleSidebarCollapsed" v-on="$listeners"/>
            </b-col>
            <b-col v-if="!isSidebarCollapsed" class="p-0 no-gutters h-100 w-100">
                <!-- Affichage des différentes fonctions après clic dans le menu (features.vue) dans ce composant-->
                <FeatureContainer />
            </b-col>
        </b-row>

        <!-- Version écran desktop -->
        <b-row v-else id="WorkbenchRow2" class="flex-grow-1 w-100 overflow-auto p-0 no-gutters">
            <splitpanes class="default-theme h-100" :dbl-click-splitter="false"
                        @splitter-click="isSidebarCollapsed = !isSidebarCollapsed"
                        @resize="spanResizing(true)"
                        @resized="spanResizing(false)">
                <!-- La largeur est calculée avec la taille par défaut (55px). Flex-shrink est mis à 0 pour empêcher la pan de s'étendre lorsque la barre vide est affichée -->
                <pane id="LeftPane-EmptySidebar" v-if="!isSidebarCollapsed" size="3" class="flex-shrink-0" style="width: 55px;">
                    <EmptySidebar v-on:ToggleSidebarCollapsedEvent="toggleSidebarCollapsed" />
                </pane>
                <pane id="LeftPane-FullSidebar" v-show="isSidebarCollapsed" size="20" min-size="10" max-size="40">
                    <!-- Menu contextuel sur l'arborescence -->
                    <hsc-menu-style-custom class="w-100 h-100">
                        <!-- menuZIndex élevé pour que le menu apparaisse au-dessus de tout composant -->
                        <hsc-menu-context-menu :menuZIndex="1000" ref="contextMenu" class="h-100 w-100 d-block">
                            <!-- KeepAlive permet de garder en cache tout ce qui est dans FullSidebar (évite notamment le rechargement des Features) plutôt que de la réinstancier lorsque que l'on bascule entre l'EmptySidebar et la FullSidebar -->
                            <FullSidebar :ManageGrouping="currentNode.ObjectType === baseObjectTypeEnum.Client" :InGrouping="inGrouping" @ShowTooltip="showTooltip" @HideTooltip="hideTooltip" @contextmenu.stop.prevent @DisplayContextMenuEvent="(...args) => contextMenuHandler(...args, saveMode!==saveModeEnum.NewObject)" />
                            <!-- Menu contextuel avec menu de copie -->
                            <template v-if="selectMode===selectModeEnum.Single"
                                slot="contextmenu" >
                                <copy-data-menu-item :CopyMenu.sync="copyInfoMenu" :Variant="Variant" :key="currentNode.Id"/>
                                 <!-- Menu contextuel disponible uniquement pour la fiche objet et si droit en écriture -->
                                <template v-if="$store.getters['GetFeaturePath'] == $store.getters['GetObjectDisplayingFeaturePath'] && $store.getters['CheckAction']('Write', 'ObjectSheet')">
                                    <hsc-menu-separator/>
                                    <hsc-menu-item :label="$t('Rename')" v-if="currentNode.ObjectType !== baseObjectTypeEnum.Client && !IsBusinessObject(currentNode.ObjectType) && currentNode.IsEditable">
                                        <div slot="body" @click="menuItemRename">
                                            <font-awesome-icon icon="pen" class="pr-2" />{{ $t('Rename') }}
                                        </div>
                                    </hsc-menu-item>
                                    <hsc-menu-item :label="$t('Delete')" v-if="currentNode.ObjectType !== baseObjectTypeEnum.Client && currentNode.IsEditable && $store.getters['CheckAction']('Delete', 'ObjectSheet')">
                                        <div slot="body" @click="menuItemDelete">
                                            <font-awesome-icon icon="x" class="pr-2" />{{ $t('Delete') }}
                                        </div>
                                    </hsc-menu-item>
                                    <!-- Utilisation du composant DepreciateObject. La dépreciation/appréciation est déterminé en fonction de la prop SelectedIsDepreciated -->
                                    <hsc-menu-item :label="$t('Depreciate')" v-if="currentNode && Object.keys(currentNode).length > 0 && !(currentNode.Depreciated) && currentNode.ObjectType !== baseObjectTypeEnum.Client && !IsBusinessObject(currentNode.ObjectType) && currentNode.IsEditable">
                                        <div slot="body" @click="displayDepreciateModal = true">
                                            <b-icon icon="alarm" />&nbsp;&nbsp;{{ $t('Depreciate') }}
                                        </div>
                                    </hsc-menu-item>
                                    <!-- Utilisation du composant DepreciateObject. La dépreciation/appréciation est déterminé en fonction de la prop SelectedIsDepreciated -->
                                    <hsc-menu-item :label="$t('Appreciate')" v-if="currentNode && Object.keys(currentNode).length > 0 && currentNode.Depreciated && currentNode.ObjectType !== baseObjectTypeEnum.Client && !IsBusinessObject(currentNode.ObjectType) && currentNode.IsEditable">
                                        <div slot="body" @click="displayDepreciateModal = true">
                                            <b-icon icon="alarm" />&nbsp;&nbsp;{{ $t('Appreciate') }}
                                        </div>
                                    </hsc-menu-item>
                                    <hsc-menu-separator v-if="currentNode.ObjectType !== baseObjectTypeEnum.Client && currentNode.IsEditable" v-show="displayNewObjectMenu" />
                                    <hsc-menu-item :label="$t('Copy')" v-if="currentNode.ObjectType !== baseObjectTypeEnum.Client && !IsBusinessObject(currentNode.ObjectType)">
                                    <div slot="body" @click="menuItemCopy">
                                        <font-awesome-icon icon="copy" class="pr-2" />{{ $t('Copy') }}
                                    </div>
                                    </hsc-menu-item>
                                    <hsc-menu-item :label="$t('Paste')" v-if="currentNode.ObjectType !== baseObjectTypeEnum.Client && !IsBusinessObject(currentNode.ObjectType)" :disabled="copiedNodeId === ''">
                                        <div slot="body" v-on="copiedNodeId === '' ? undefined : { click: () => menuItemPaste() }">
                                            <font-awesome-icon icon="paste" class="pr-2" />{{ $t('Paste') }}
                                        </div>
                                    </hsc-menu-item>
                                    <hsc-menu-separator v-if="currentNode.ObjectType !== baseObjectTypeEnum.Client && !IsBusinessObject(currentNode.ObjectType) && currentNode.IsEditable" />
                                    <!-- Utilisation de v-show pour que l'instance d'ObjectMenu reste active même si masquée -->
                                    <hsc-menu-item :label="$t('New')" v-if="$store.getters['CheckAction']('Add', 'ObjectSheet')" v-show="displayNewObjectMenu">
                                        <div slot="body">
                                            <span class="pr-2" />&nbsp;&nbsp;&nbsp;&nbsp;{{ $t('New') }}
                                        </div>
                                        <ObjectMenu :Menu="contextMenuItems" :Constraints="constraints" :ObjectType="currentNode.ObjectType" :IsFirstLevelMenus.sync="displayNewObjectMenu"
                                            :ParentNode="currentNode"
                                            v-on:CloseObjectMenuEvent="$refs.contextMenu.$refs.menu.close(true)" />
                                    </hsc-menu-item>
                                </template>
                            </template>
                            
							<!-- Menu de sélection par type d'objets sur clic-droit en multi-noeuds -->
                            <template v-else-if="selectMode!==selectModeEnum.Single" slot="contextmenu">
                                <FilterMenu :FilterNode="currentNode" :Menu="contextMenuItems" :Variant="Variant" />
                            </template>
                        </hsc-menu-context-menu>
                    </hsc-menu-style-custom>
                </pane>
                <pane id="RightPane" :size="isSidebarCollapsed ? 80 : 97" :min-size="isSidebarCollapsed ? 60 : 97" :max-size="isSidebarCollapsed ? 90 : 97" class="p-0" >
                    <!-- Bouton de masquage de la sidebar -->
                    <!-- <b-button v-b-tooltip.hover.bottom :title="isSidebarCollapsed ? 'Masquer' : 'Afficher l\'arborescence'" @click="toggleSidebarCollapsed()" variant="outline-gray"
                        class="bg-white position-absolute py-2 px-1" style="z-index: 1000 !important; top: calc(50% + 30px);">
                        <b-icon v-if="isSidebarCollapsed" icon="caret-left-fill" variant="gray-dark"  />
                        <b-icon v-else icon="caret-right-fill" variant="gray-dark"  />
                    </b-button> -->

                    <b-container @dragover.prevent="showTooltip($event, false)" @drop.prevent="onDrop($event)" fluid class="d-flex flex-column mh-100 h-100 p-0 no-gutters">
                        <!-- Affichage des regroupements -->
                        <Grouping v-if="inGrouping" @ExitGroupingEvent="inGrouping=false" />

                        <!-- Affichage des différentes fonctions après clique dans le menu (features.vue) dans ce composant-->
                        <FeatureContainer v-else :Variant="Variant"/>
                    </b-container>
                </pane>
            </splitpanes>
        </b-row>
        <!-- Composant Déprécier/apprécier un objet -->
        <DepreciateObject @CloseDepreciateModal="closeDepreciateModal" :NodeId="currentNode?.Id" :NodeIsDepreciated="currentNode?.Depreciated" :ObjectType="currentNode?.ObjectType" :DisplayDepreciateModal="displayDepreciateModal" />
        <b-modal v-model="renameModal" centered>
            <template #modal-header="{ }">
                <h6>{{ $t('Rename') }}</h6>
            </template>
            <b-form-group :label="$t('NewName')"
                          label-cols-sm="4"
                          label-for="newName"
                          label-class="pt-0 pb-0 pr-0"
                          label-align-sm="left">
                <!-- description="" -->
                <b-input-group id="newName" size="sm">
                    <b-form-input v-model="newName" autofocus ref="newName" />
                </b-input-group>
            </b-form-group>

            <template #modal-footer="{ cancel }">
                <b-button @click="cancel()" :variant="'outline-' + Variant" pill>{{ $t('Cancel') }}</b-button>
                <b-button @click="renameNode()" :variant="Variant" pill>{{ $t('OK') }}</b-button>
            </template>
        </b-modal>

        <!-- Info-bulle indiquant si le drop d'objet de l'arborescence est autorisé ou non (affichage géré via la fonction showTooltip() dans la section <script></script>)-->
        <div id="feedBackTooltip" class="feedBackTooltip"/>
    </b-container>
</template>

<script lang="ts">
    import { Component, Provide, ProvideReactive, Vue, Watch } from 'vue-property-decorator';
    import { mixins } from 'vue-class-component';
    import { Getter } from 'vuex-class';
    import Dictionary from '@/types/Dictionary';
    import { API_URL, MAIN_FILTER_TREE } from '@/App.settings';
    import { ContextMenu, LinkUtils, ObjectUtils, ScreenUtils } from "@/components/commons/Utils";

    /** Splitter */
    import { Splitpanes, Pane } from 'splitpanes';
    import 'splitpanes/dist/splitpanes.css'

    /** Sidebar */
    import FullSidebar from "./Workbench/LeftPane/FullSidebar.vue";
    import EmptySidebar from "./Workbench/LeftPane/EmptySidebar.vue";

    /** Menu contextuel sur clique droit sur l'arborescence */
    import ObjectMenu from "@/components/commons/ObjectMenu.vue";
	import FilterMenu from "@/components/commons/FilterMenu.vue";
    import HscMenuStyleCustom from '@/components/commons/HscMenuStyleCustom';
    import DepreciateObject from "@/components/commons/DepreciateObject.vue";

    import Navbar from "./Workbench/Navbar.vue";
    import FeatureContainer from "./Workbench/RightPane/FeatureContainer.vue";
    import Grouping from "./Workbench/RightPane/Grouping.vue";
    import FilterT from '@/types/FilterTree/FilterT';

    import BaseObjectTypeEnum from '@/enums/BaseObjectTypeEnum';

    /** Menu des fonctions  */
    import Features from './Workbench/Features.vue';

    import locales from './locales/Workbench.json';

    import SaveModeEnum from '@/enums/SaveModeEnum';
	import SelectModeEnum from '@/enums/SelectModeEnum';

    /** Médiateur de communication inter-composants/stores VueX dédié à la navigation */
    import { NavigationMediator } from '@/mediators/NavigationMediator';
    import { StoreMediator } from '@/mediators/StoreMediator';
    import TreeNode from '@/types/TreeNode';
    import INodeSelector from '@/interfaces/INodeSelector';
    import CopyInfoMenu from '@/types/CopyInfoMenu';
import axios from 'axios';
import CopyDataMenuItem from '../commons/FilterMenu/CopyDataMenuItem.vue';

    /** Composant affichant le mode Espace de travail */
    @Component({
        components: {
            /** Splitter */
            Splitpanes, Pane,

            /** Sidebar */
            FullSidebar,
            EmptySidebar,

            Navbar,
            FeatureContainer,
            Grouping,

            /** Menu contextuel sur clique droit l'arborescence */
            ObjectMenu,
			FilterMenu,
            CopyDataMenuItem,
            DepreciateObject,
            'hsc-menu-style-custom': HscMenuStyleCustom("secondary"),

            /** Menu des fonctions */
            Features,
        },
        i18n: { messages : locales }
    })
    export default class Workbench extends mixins(Vue, LinkUtils, ScreenUtils, ObjectUtils, ContextMenu) implements INodeSelector {
        /** Nécessaire pour rendre accessible l'enum BaseObjectTypeEnum dans la section template */
        private baseObjectTypeEnum = BaseObjectTypeEnum;
		private selectModeEnum = SelectModeEnum;
        private saveModeEnum = SaveModeEnum;

        /** Nom store Vuex à passer à HeritageTree. Variable nécessaire pour utilisation dans la section Template */
        private filterTreeStore = MAIN_FILTER_TREE;

        /** Indique le nom de l'instance de FilterTree à utiliser comme base de clonage pour tous les composants enfants du Workbench */
        @ProvideReactive()
		private sourceFilterTree: string = this.filterTreeStore;

        /** Variant de couleur à appliquer */
        @ProvideReactive()
        private Variant: string = "secondary";

        /** Mode de sélection de la fonction en cours */
        @Getter(MAIN_FILTER_TREE + '/GetSelectMode')
        private selectMode!: SelectModeEnum;

        /** Médiateur de communication inter-composants/stores VueX dédié à la navigation */
        @Provide()
        private navigationMediator: NavigationMediator = new NavigationMediator(this);

        private singleNode!: TreeNode;

        public SetNode(node: TreeNode): Promise<any> {
            return new Promise(async (resolve, reject) => {
                this.singleNode = node;

                resolve('SingleNode affecté !');
            });
        }

        /** Détermine si la sidebar (composant FullSidebar) est déployée ou rétractée */
        private isSidebarCollapsed: boolean = true;

        /** Indique le regroupement en cours sur l'instance d'arborescence principale à tous les composants enfants */
		@Getter(MAIN_FILTER_TREE + '/GetGrouping')
        @ProvideReactive()
		private Grouping!: FilterT;

        /** Indique si on est en mode regroupement ou non */
        private inGrouping: boolean = false;

        @Getter('ObjectStore/CopiedNodeId')
        private copiedNodeId!: string;

		/** Item source du drag and drop  */
		@Getter('ObjectMoveStore/GetSourceObjectType')
		private sourceObjectType!: string;

		/** Item source du drag and drop  */
		@Getter('ObjectMoveStore/GetSourceObjectName')
		private sourceObjectName!: string;

		/** Indique le mode de sauvegarde en cours dans la fiche objet */
		@Getter('ObjectStore/GetSaveMode')
		private saveMode!: SaveModeEnum;

        /** Ouvre ou ferme la sidebar (composant FullSidebar) contenant l'arborescence */
        private toggleSidebarCollapsed() {
            this.isSidebarCollapsed = !this.isSidebarCollapsed;
        }

        //#region Gestion de la dépréciation d'objet
        private selectedIsDepreciated: boolean = false;
        private displayDepreciateModal: boolean = false;

        /** Menu pour copier des informations sur le noeud (nom, Invariant...) dans le press papier*/
		private copyInfoMenu: CopyInfoMenu = null ;

        /** Fermeture de la fenêtre modale de dépréciation d'un objet */
        private closeDepreciateModal(successDeprecitation: boolean) {
            this.displayDepreciateModal = false;
            this.selectedIsDepreciated = successDeprecitation;
        }
        //#endregion Gestion de la dépréciation d'objet
        
        //#region Gestion info-bulle affichée sur drag'n'drop dans l'arborescence
        /** Masquage de l'info-bulle affichée sur drag'n'drop dans l'arborescence */
        private hideTooltip() {
            const element = document.getElementById('feedBackTooltip');
            element.style.display = 'none';
        }

        /** Affichage de l'info-bulle sur drag'n'drop dans l'arborescence
         * @description Déclenchée depuis le composant HeritageNode
         * @event
         * @param authorization Indique si le drop est authorisée ou non ==> affiche le signe + dans l'info-bulle si authorisée ou le signe - si pas authorisée
         */
        private showTooltip(event, authorization: boolean) {
            const element = document.getElementById('feedBackTooltip');
            if(this.sourceObjectName && this.sourceObjectType){
                element.style.display = 'block';
                element.style.left = event.pageX + 'px';
                element.style.top = (event.pageY - 20) + 'px';
                element.classList.add('pr-1');
                element.classList.add('align-items-center');
                element.replaceChildren(...[]);
                if(!element.hasChildNodes()){
                    let img = document.createElement("img");
                    let icn = document.createElement("i");
                    icn.classList.add('bi');
                    if(authorization){
                        icn.classList.add('bi-plus-circle');
                        icn.setAttribute("style", "color: green");
                    }
                    else
                    {
                        icn.classList.add('bi-slash-circle');
                        icn.setAttribute("style", "color: red");
                    }
                    img.style.padding = '6px'
                    img.src = this.AbsoluteLink('Icons/' + this.sourceObjectType + '_Icon.svg');
                    let text = document.createTextNode(this.sourceObjectName);
                    element.appendChild(icn);
                    element.appendChild(img);
                    element.appendChild(text);
                }
            }
        }

        /** Sur relachement du drag'n'drop dans l'arborescence */
        private onDrop(evt) {
            this.$store.dispatch("ObjectMoveStore/EndMoveObject");
        }
        //#endregion Gestion info-bulle affiché sur drag'n'drop dans l'arborescence

        //#region Gestion menu contextuel

        /** 
         * Indique si l'item Nouveau du menu contextuel est affiché ou non
         * @description Il est affiché uniquement si des objets peuvent être créés à partir du noeud sélectionné selon les contraintes traités dans d'ObjetMenu.vue
         */
        private displayNewObjectMenu: boolean = false;

        //#region Gestion du copier/coller
        private menuItemCopy() {
            // Fermeture du menu contextuel
            (this.$refs as any).contextMenu.$refs.menu.close(true);

            this.$store.dispatch("ObjectStore/Copy", { nodeId: this.currentNode?.Id }).then(x => {
                this.$bvToast.toast(this.$t('SelectedObjectCopied').toString(), { // "L'objet sélectionné a été copié !"
                    title: this.$t('ObjectCopy').toString(), // "Copie objet",
                    autoHideDelay: 3000,
                    variant: "info",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
            })
            .catch(err => {
                console.error(err);
                this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // "Une erreur s'est produite."
                    title: this.$t('ObjectCopy').toString(), // "Copie objet",
                    autoHideDelay: 10000,
                    variant: "danger",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
            });
        }

        private menuItemPaste() {
            // Fermeture du menu contextuel
            (this.$refs as any).contextMenu.$refs.menu.close(true);

            this.$store.dispatch("ObjectStore/Paste", { destRef: this.currentNode?.Id })
                .then(x => {
                    this.$bvToast.toast(this.$t('SelectedObjectCopied').toString(), { // "L'objet sélectionné a été copié !"
                        title: this.$t('ObjectCopy').toString(), // "Copie objet",
                        autoHideDelay: 3000,
                        variant: "info",
                        solid: true,
                        appendToast: true,
                        toaster: "b-toaster-top-center"
                    });
                })
                .catch(err => {
                    console.error(err);
                    this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // "Une erreur s'est produite."
                        title: this.$t('ObjectCopy').toString(), // "Copie objet",
                        autoHideDelay: 10000,
                        variant: "danger",
                        solid: true,
                        appendToast: true,
                        toaster: "b-toaster-top-center"
                    });
                });
        }
        //#endregion Gestion du copier/coller
        //#region Gestion du renommage
        private renameModal: boolean = false;
        private newName: string = "";

        @Watch('renameModal')
        private initName() {
            this.newName = this.currentNode?.Name;
        }

        private menuItemRename() {
            this.renameModal = true;
            (this.$refs as any).contextMenu.$refs.menu.close(true);
        }

        private renameNode() {
            this.$store.dispatch("ObjectStore/Rename", { nodeId: this.currentNode?.Id, newName: this.newName }).then(x => {
                this.renameModal = false;
                this.$bvToast.toast(this.$t('SelectedObjectRenamed').toString(), { //  "L'objet sélectionné a été renommé avec succès !"
                    title: this.$t('ObjectRename').toString(),// "Renommage objet",
                    autoHideDelay: 3000,
                    variant: "success",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
            })
            .catch(err => {
                console.error(err);
                this.$bvToast.toast(this.$t('AnErrorOccured').toString(), {
                    title: this.$t('ObjectRename').toString(),// "Renommage objet",
                    autoHideDelay: 10000,
                    variant: "danger",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
            });
        }
        //#endregion Gestion du renommage

        /** Suppression du noeud/objet sélectionné dans l'arborescence */
        private menuItemDelete() {
            this.delete(this.currentNode?.Id, true);
        }

        /** Contraintes entre objets */
        @Getter('MenuStore/GetConstraints')
        private constraints!: Dictionary<any>;

        //#endregion Gestion menu contextuel

        /**
         * Indique si un redimenssionnement est du splitspan en cours dans Workbench.vue.
         * ==> Utilisé pour informer IFrameLoaderFiltered afin de désactiver les events Ext.net dans l'IFrame qui intérfère avec les events de vue.js
         * @param value
         */
        private spanResizing(value: boolean) {
            this.$store.dispatch("SetSpanResizing", value);
        }

        @Watch('currentNode', { immediate: true })
		private async updateMenu() {
			if(this.currentNode.Id != undefined){
				const args: Dictionary<any> = {};
                args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
                args["ObjectId"] = this.currentNode.Id;

                await axios.post(API_URL + "/object/InitContextCopyMenu", args).then((response) => {
                    if (response && response.data) {
                        this.copyInfoMenu = response.data;
                        console.debug(`FilterMenu - loadCopyMenu : copyMenu ${JSON.stringify(this.copyInfoMenu)}`)
                    }
                });
			}
		}

        mounted() {
            StoreMediator.GetInstance().SetNavigationMediator(this.navigationMediator);
        }
	}
</script>

<style scoped>
    /** css de la version 3 de splitpanes (thème par défaut plus visible) */
    /* @import 'https://unpkg.com/splitpanes/dist/splitpanes.css'; */
    
    .feedBackTooltip {
        position: absolute;
        background-color: #fff;
        color: black;
        padding-left: 5px;
        padding-right: 5px;
        display: none;
        pointer-events: none;
        z-index: 100;
        border: solid;
        border-width: 1px;
        border-radius: 8px;
        border-color: black;
    }
</style>